var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/**
 * An interface for interacting with and modifying elements according to the
 * {@link https://getbem.com/ BEM specification}.
 *
 * This function creates a {@link BaseBEMType} object. The function takes an Block name which is
 * applied to the Block element as a class and provides methods and properties for interacting with
 * elements on the page that are associated with this element.
 * @param blockName
 * @returns A {@link BaseBEMType} object that can interact with all Block elements with the given
 *          `blockName`, or can be called with an Element name and/or Modifiers for further
 *          narrowing down the set of elements it selects.
 *
 * @example
 * ```ts
 * const bem = BEM("some-block-component");
 * bem("a-nested-element").className
 * // "some-block-component__a-nested-element"
 * bem({ modifier1: true, modifier2: false, modifier3: "value" }).className
 * // "some-block-component some-block-component--modifier1 some-block-component--modifier3-value";
 * bem("another-element", { modifier4: true }).classes
 * // ["some-block-component__another-element", "some-block-component__another-element--modifier4"]
 * bem("yet-element").select()
 * // An Array of elements with class name "some-block-component__yet-element"
 * bem("and-another").modify({ modifier1: false, modifier2: true });
 * // Modifies ".some-block-component__and-another" elements:
 * // 1. Removes any "some-block-component__and-another--modifier1" classes, if any
 * // 2. Adds a "some-block-component__and-another--modifier2" class
 * ```
 */
var BEM = function (blockName) {
    // Make any empty calls to the specifier (i.e., `specifier()`) return the same object instead of
    // creating a new one each time.
    var emptySpecifier = null;
    var specifier = function (elementName, modifiers) {
        var _a;
        // Check if only a modifier list/dict was provided, and assume "" for the element name.
        if (typeof elementName === "object" && elementName !== null) {
            modifiers = elementName;
            elementName = "";
        }
        if (elementName == null) {
            elementName = "";
        }
        if (typeof elementName === "number") {
            elementName = elementName.toString();
        }
        var qualifiedElementName = "".concat(blockName).concat(elementName === "" ? "" : "__" + elementName);
        if (modifiers == null) {
            modifiers = [];
        }
        else if (!Array.isArray(modifiers)) {
            modifiers = Object.entries(modifiers).flatMap(function (_a) {
                var _b = __read(_a, 2), modifier = _b[0], value = _b[1];
                return value == null || value === false
                    ? []
                    : value === true
                        ? [modifier]
                        : ["".concat(modifier, "-").concat(value)];
            });
        }
        var mappedModifiers = modifiers.map(function (modifier) { return "".concat(qualifiedElementName, "--").concat(modifier); });
        // Use the `emptySpecifier` if this function was called with no specifying query.
        if (elementName === "" &&
            modifiers.length === 0 &&
            emptySpecifier != null) {
            return emptySpecifier;
        }
        // Set to `null` if `unlock`ed and a set Array of elements (that get returned every time) if
        // `lock`ed.
        var lockedCollection = null;
        // Calculate the list of classes and freeze the Array to prevent tampering.
        var classes = Object.freeze([qualifiedElementName].concat(mappedModifiers));
        // Just join the classes to generate the className.
        var className = classes.join(" ");
        var select = function () {
            return Array.from(lockedCollection
                ? lockedCollection
                : document.getElementsByClassName(className));
        };
        var modify = function (newModifiers, elements) {
            var e_1, _a;
            var elementIter = elements == null
                ? select()
                : elements instanceof Element
                    ? [elements]
                    : elements;
            for (var modifier in newModifiers) {
                // Determine the new modifier class to add.
                var value = newModifiers[modifier];
                var newClass = value == null || value === false || value === ""
                    ? null
                    : value === true
                        ? modifier
                        : "".concat(modifier, "-").concat(value);
                // Remove any other classes that match this modifier.
                var re = new RegExp("(?:^| )".concat(qualifiedElementName, "--").concat(modifier, "(?:-[^ ]*)?(?:$| )"));
                try {
                    for (var elementIter_1 = (e_1 = void 0, __values(elementIter)), elementIter_1_1 = elementIter_1.next(); !elementIter_1_1.done; elementIter_1_1 = elementIter_1.next()) {
                        var element = elementIter_1_1.value;
                        var match = void 0;
                        while ((match = element.className.match(re))) {
                            element.classList.remove(match[0].trim());
                        }
                        if (newClass != null) {
                            element.classList.add("".concat(qualifiedElementName, "--").concat(newClass));
                        }
                    }
                }
                catch (e_1_1) { e_1 = { error: e_1_1 }; }
                finally {
                    try {
                        if (elementIter_1_1 && !elementIter_1_1.done && (_a = elementIter_1.return)) _a.call(elementIter_1);
                    }
                    finally { if (e_1) throw e_1.error; }
                }
            }
        };
        var lock = function () {
            lockedCollection = Array.from(select());
        };
        var unlock = function () {
            lockedCollection = null;
        };
        var root = function (element) {
            for (var current = element; current instanceof Element; current = current.parentNode) {
                if (current.classList.contains(blockName)) {
                    return current;
                }
            }
            return null;
        };
        return _a = {
                classes: classes,
                className: className,
                select: select,
                modify: modify,
                lock: lock,
                unlock: unlock,
                root: root
            },
            _a[Symbol.iterator] = function () { return select()[Symbol.iterator](); },
            _a.toString = function () { return className; },
            _a;
    };
    emptySpecifier = specifier();
    // Merge the `emptySpecifier` (which is of type `NestedBEMType`) into the `specifier` so that it
    // can be made into a `BaseBEMType`, which is a combination of the two.
    Object.assign(specifier, emptySpecifier);
    return specifier;
};
// Additionally export a set of already-made BEM objects for common names we use throughout multiple
// files.
export var button = BEM("button");
export var pageContent = BEM("page-content");
export var imgAspectRatio = BEM("img-aspect-ratio");
BEM.button = button;
BEM.pageContent = pageContent;
BEM.imgAspectRatio = imgAspectRatio;
export default BEM;
